import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Chip from "@mui/material/Chip";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";
import { libelleVilleDate } from "utils/oumrahVilleDepart";
import PlacementChambres from "components/PlacementChambres";

const typesInfo = {
  double: { label: "Chambres doubles", cap: 2, color: "#1A73E8", icon: "hotel" },
  triple: { label: "Chambres triples", cap: 3, color: "#7c3aed", icon: "hotel" },
  quadruple: { label: "Chambres quadruples", cap: 4, color: "#fb8c00", icon: "hotel" },
};

function OumrahHebergement() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [besoins, setBesoins] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popupType, setPopupType] = useState(null);

  useEffect(() => {
    api.get("/oumrah/sessions").then((res) => setSessions(res.data)).catch(() => {});
  }, []);

  const chargerBesoins = (silencieux = false) => {
    if (!sessionId) { setBesoins(null); return; }
    if (!silencieux) setLoading(true);
    api.get("/oumrah/hebergement/" + sessionId + "/besoins-chambres")
      .then((res) => setBesoins(res.data))
      .catch(() => setBesoins(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { chargerBesoins(); }, [sessionId]);

  const carteType = (type) => {
    if (!besoins) return null;
    const info = typesInfo[type];
    const d = besoins[type];
    return (
      <Grid item xs={12} md={4} key={type}>
        <Card sx={{ p: 3, height: "100%", borderTop: "4px solid " + info.color, cursor: "pointer", transition: "box-shadow .2s", "&:hover": { boxShadow: 4 } }}
          onClick={() => setPopupType(type)}>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <Icon sx={{ color: info.color }}>{info.icon}</Icon>
            <MDTypography variant="h6">{info.label}</MDTypography>
          </MDBox>
          <MDTypography variant="h2" fontWeight="bold" sx={{ color: info.color }}>
            {d.chambres}
          </MDTypography>
          <MDTypography variant="button" color="text" display="block" mb={2}>
            chambre(s) créée(s)
          </MDTypography>
          <MDBox sx={{ borderTop: "1px solid #f0f0f0", pt: 1.5 }}>
            {type === "double" && d.couples > 0 && (
              <MDTypography variant="caption" color="text" display="block">
                · {d.couples} couple(s) marié(s)
              </MDTypography>
            )}
            <MDTypography variant="caption" color="text" display="block">
              · {d.hommes} homme(s){type === "double" ? " seul(s)" : ""}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              · {d.femmes} femme(s){type === "double" ? " seule(s)" : ""}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mt={0.5} fontWeight="bold">
              {d.total_personnes} personne(s) au total
            </MDTypography>
          </MDBox>
        </Card>
      </Grid>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDTypography variant="h5" mb={1}>Hébergement — Chambres</MDTypography>
              <MDTypography variant="button" color="text" mb={2} display="block">
                Sélectionnez une session pour voir le nombre de chambres créées, à demander à l'hôtel.
                Participants comptés : inscriptions validées et au-delà.
              </MDTypography>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel id="sess-heberg">Session</InputLabel>
                <Select labelId="sess-heberg" label="Session" sx={{ height: 44 }}
                  value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                  <MenuItem value="">—</MenuItem>
                  {sessions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.libelle}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Card>
          </Grid>

          {loading && (
            <Grid item xs={12}>
              <MDTypography variant="button" color="text">Chargement…</MDTypography>
            </Grid>
          )}

          {besoins && !loading && (
            <>
              {besoins.sans_sexe > 0 && (
                <Grid item xs={12}>
                  <Card sx={{ p: 2, bgcolor: "#fff3e0", border: "1px solid #ffb74d" }}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ color: "#e65100" }}>warning</Icon>
                      <MDTypography variant="button" sx={{ color: "#e65100" }}>
                        {besoins.sans_sexe} participant(s) sans sexe renseigné — non pris en compte dans la répartition hommes/femmes. Renseignez leur sexe pour une répartition complète.
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              )}

              {["double", "triple", "quadruple"].map((t) => carteType(t))}

              <Grid item xs={12}>
                <Card sx={{ p: 3, bgcolor: "#f5f7fa" }}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                    <MDBox>
                      <MDTypography variant="h6">Total à demander à l'hôtel</MDTypography>
                      <MDTypography variant="caption" color="text">
                        {besoins.total_participants} participant(s) hébergé(s)
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="h3" fontWeight="bold" color="info">
                      {besoins.total_chambres} chambre(s)
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            </>
          )}
        </Grid>

        <PlacementChambres sessionId={sessionId} onChambresChange={() => chargerBesoins(true)} />
      </MDBox>

      <Dialog open={!!popupType} onClose={() => setPopupType(null)} fullWidth maxWidth="sm">
        {popupType && besoins && (() => {
          const d = besoins[popupType];
          const personnes = d.personnes || [];
          const couples = []; const hommes = []; const femmes = []; const vus = new Set();
          personnes.forEach((p) => {
            if (vus.has(p.id)) return;
            const conj = personnes.find((x) => x.id === p.conjoint_id);
            if (popupType === "double" && conj) {
              couples.push([p, conj]); vus.add(p.id); vus.add(conj.id);
            } else if (p.sexe === "H") { hommes.push(p); vus.add(p.id); }
            else if (p.sexe === "F") { femmes.push(p); vus.add(p.id); }
            else { vus.add(p.id); }
          });
          const nom = (p) => (p.prenom || "") + " " + (p.nom ? p.nom.toUpperCase() : "");
          const depart = (p) => " — " + libelleVilleDate(p.ville_depart, p.ville_depart_date);
          return (
            <>
              <DialogTitle>{typesInfo[popupType].label} — {personnes.length} personne(s)</DialogTitle>
              <DialogContent dividers>
                {couples.length > 0 && (
                  <MDBox mb={2}>
                    <MDTypography variant="button" fontWeight="bold" color="text" display="block" mb={1}>
                      Couples mariés ({couples.length})
                    </MDTypography>
                    {couples.map(([a, b], idx) => (
                      <MDBox key={idx} display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Chip size="small" label="Couple" sx={{ bgcolor: "#f5f3ff", color: "#7c3aed" }} />
                        <MDTypography variant="caption">{nom(a)} &amp; {nom(b)}{depart(a)}</MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>
                )}
                {hommes.length > 0 && (
                  <MDBox mb={2}>
                    <MDTypography variant="button" fontWeight="bold" color="text" display="block" mb={1}>
                      Hommes ({hommes.length})
                    </MDTypography>
                    {hommes.map((p) => (
                      <MDTypography key={p.id} variant="caption" display="block">· {nom(p)}{depart(p)}</MDTypography>
                    ))}
                  </MDBox>
                )}
                {femmes.length > 0 && (
                  <MDBox mb={2}>
                    <MDTypography variant="button" fontWeight="bold" color="text" display="block" mb={1}>
                      Femmes ({femmes.length})
                    </MDTypography>
                    {femmes.map((p) => (
                      <MDTypography key={p.id} variant="caption" display="block">· {nom(p)}{depart(p)}</MDTypography>
                    ))}
                  </MDBox>
                )}
                {personnes.length === 0 && (
                  <MDTypography variant="caption" color="text">Aucune personne dans ce type.</MDTypography>
                )}
              </DialogContent>
              <MDBox p={2} display="flex" justifyContent="flex-end">
                <MDButton color="secondary" onClick={() => setPopupType(null)}>Fermer</MDButton>
              </MDBox>
            </>
          );
        })()}
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default OumrahHebergement;
